import { authenticatedFetch } from "@lumi/data";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../../../auth/AuthContext";
import { createRefreshCallback } from "../../../../auth/refreshCallback";
import type { BulkEditTitle, CastMember, CrewMember, TitleAnalytics } from "../types";

/**
 * Hook to fetch all titles for bulk editing
 * Uses admin endpoint to get full data including unpublished titles
 */
export function useBulkTitles() {
  const { accessToken, refreshAuth } = useAuth();
  const [titles, setTitles] = useState<BulkEditTitle[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTitles = useCallback(async () => {
    if (!accessToken) {
      setTitles([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

      // Fetch from admin titles endpoint (includes all statuses)
      const data = await authenticatedFetch<BulkEditTitle[]>(
        `${baseUrl}/v1/admin/titles`,
        { method: "GET" },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      setTitles(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to fetch titles";
      setError(message);
      console.error("Error fetching titles for bulk edit:", err);
    } finally {
      setLoading(false);
    }
  }, [accessToken, refreshAuth]);

  useEffect(() => {
    void fetchTitles();
  }, [fetchTitles]);

  // Optimistically update a title in local state
  const updateTitleLocally = useCallback((titleId: string, updates: Partial<BulkEditTitle>) => {
    setTitles((prev) => prev.map((title) => (title.id === titleId ? { ...title, ...updates } : title)));
  }, []);

  // Merge stats into existing titles
  const mergeStats = useCallback((statsMap: Map<string, TitleAnalytics>) => {
    setTitles((prev) =>
      prev.map((title) => ({
        ...title,
        stats: statsMap.get(title.id) || title.stats,
      })),
    );
  }, []);

  // Fetch and update a single title (useful after image uploads to get presigned URLs)
  const refreshSingleTitle = useCallback(
    async (titleId: string) => {
      if (!accessToken) return;

      try {
        const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";
        const updatedTitle = await authenticatedFetch<BulkEditTitle>(
          `${baseUrl}/v1/admin/titles/${titleId}`,
          { method: "GET" },
          accessToken,
          createRefreshCallback(refreshAuth),
        );

        // Update just this title in local state
        setTitles((prev) => prev.map((title) => (title.id === titleId ? updatedTitle : title)));
      } catch (err) {
        console.error("Error refreshing single title:", err);
        // Silently fail - not critical
      }
    },
    [accessToken, refreshAuth],
  );

  return { titles, loading, error, refetch: fetchTitles, updateTitleLocally, mergeStats, refreshSingleTitle };
}

/**
 * Hook to fetch analytics stats for titles
 * Called separately to avoid re-fetching all title data
 */
export function useTitleStats() {
  const { accessToken, refreshAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async (): Promise<Map<string, TitleAnalytics>> => {
    if (!accessToken) {
      return new Map();
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

      const data = await authenticatedFetch<BulkEditTitle[]>(
        `${baseUrl}/v1/admin/titles?includeStats=true`,
        { method: "GET" },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      // Convert to Map for easy lookup
      const statsMap = new Map<string, TitleAnalytics>();
      for (const title of data) {
        if (title.stats) {
          statsMap.set(title.id, title.stats);
        }
      }

      return statsMap;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to fetch stats";
      setError(message);
      console.error("Error fetching title stats:", err);
      return new Map();
    } finally {
      setLoading(false);
    }
  }, [accessToken, refreshAuth]);

  return { fetchStats, loading, error };
}

/**
 * Hook to update a single field on a title
 */
export function useUpdateTitleField() {
  const { accessToken, refreshAuth } = useAuth();
  const [updating, setUpdating] = useState(false);

  const updateField = useCallback(
    async (
      titleId: string,
      field: string,
      value: string | number | boolean | string[] | CastMember[] | CrewMember[] | null,
    ) => {
      if (!accessToken) {
        throw new Error("Not authenticated");
      }

      setUpdating(true);

      try {
        const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

        await authenticatedFetch(
          `${baseUrl}/v1/admin/titles/${titleId}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ [field]: value }),
          },
          accessToken,
          createRefreshCallback(refreshAuth),
        );
      } finally {
        setUpdating(false);
      }
    },
    [accessToken, refreshAuth],
  );

  return { updateField, updating };
}
